import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { AboutThisEvent } from './AboutThisEvent';
import type { StructuredContent } from '@/types';

import { MODULE_TYPES, TEXT_ALIGNMENTS } from './AboutThisEvent/StructuredModuleRenderer/StructuredModuleRenderer';
import styles from './Overview.module.scss';
import { LISTING_HEAP_IDS, READ_MORE_MESSAGE } from '@/components/_shared/constants';
import classNames from 'classnames';

// Threshold for determining if content is "long" enough to show "Read more" button
export const LONG_CONTENT_THRESHOLD = 280;

export const getVisibleText = (html: string): string => {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isValidTextModule = (module: unknown): module is { type: string; text: string } => {
  if (module === null || typeof module !== 'object') {
    return false;
  }

  const moduleObj = module as Record<string, unknown>;

  const hasRequiredProperties = 'type' in moduleObj && 'text' in moduleObj;
  if (!hasRequiredProperties) {
    return false;
  }

  const isTextType = moduleObj.type === MODULE_TYPES.TEXT;
  const hasValidText = typeof moduleObj.text === 'string' && moduleObj.text.length > 0;

  return isTextType && hasValidText;
};

const findFirstTextModule = (modules: unknown[]) => {
  return modules.find(isValidTextModule);
};

export type OverviewProps = {
  summary?: string;
  structuredContent?: StructuredContent;
};

export const Overview: React.FC<OverviewProps> = React.memo(({ summary, structuredContent }) => {
  const { formatMessage } = useIntl();
  const summaryRef = useRef<HTMLDivElement>(null);

  const [showFullEventOverview, setShowFullEventOverview] = useState(false);
  const [hasContentOverflow, setHasContentOverflow] = useState(false);

  const handleShowFullEventOverview = useCallback(() => {
    setShowFullEventOverview(true);
  }, []);

  const handleHideFullEventOverview = useCallback(() => {
    setShowFullEventOverview(false);
  }, []);

  // Helper function to safely truncate HTML content
  const sanitizeHtmlForTruncation = (htmlContent: string): string => {
    if (!htmlContent) return '';

    // Remove any incomplete tags at the end (tags that start with < but don't end with >)
    let sanitized = htmlContent.replace(/<[^>]*$/g, '');

    // Also remove any incomplete closing tags
    sanitized = sanitized.replace(/<\/[^>]*$/g, '');

    // Remove any attributes that might be cut off (ending with incomplete quotes)
    sanitized = sanitized.replace(/\s+[a-zA-Z-]+=("[^"]*|'[^']*)$/g, '');

    return sanitized;
  };

  const sanitizedSummary = useMemo(() => {
    return sanitizeHtmlForTruncation(summary || '');
  }, [summary]);

  useEffect(() => {
    if (showFullEventOverview) return;

    const el = summaryRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasContentOverflow(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    }

    return undefined;
  }, [sanitizedSummary, structuredContent, showFullEventOverview]);

  const hasMoreContentToShow = useMemo(() => {
    const originalModules = structuredContent?.modules || [];
    const hasSummary = Boolean(sanitizedSummary);
    const firstModule = originalModules[0];

    const hasLongSummary = hasSummary && getVisibleText(sanitizedSummary).length > LONG_CONTENT_THRESHOLD;

    const hasLongTextModule = originalModules.some(
      (module) => isValidTextModule(module) && getVisibleText(module.text).length > LONG_CONTENT_THRESHOLD,
    );

    const hasMultipleModules = originalModules.length > 1;

    const isFirstModuleMedia =
      firstModule && (firstModule.type === MODULE_TYPES.IMAGE || firstModule.type === MODULE_TYPES.VIDEO);

    const hasHiddenModules = hasSummary && isFirstModuleMedia && originalModules.length > 0;

    return hasLongSummary || hasLongTextModule || hasMultipleModules || hasHiddenModules || hasContentOverflow;
  }, [sanitizedSummary, structuredContent, hasContentOverflow]);

  const structuredContentWithSummary: StructuredContent = useMemo(
    () => ({
      ...structuredContent,
      modules: [
        {
          type: MODULE_TYPES.TEXT,
          text: `<p>${sanitizedSummary}</p>`,
          alignment: TEXT_ALIGNMENTS.LEFT,
        },
        ...(structuredContent?.modules ?? []),
      ],
    }),
    [structuredContent, sanitizedSummary],
  );

  const readMoreButton = useMemo(() => {
    return hasMoreContentToShow && !showFullEventOverview ? (
      <button
        className={styles.readMore}
        onClick={handleShowFullEventOverview}
        type="button"
        data-heap-id={LISTING_HEAP_IDS.ABOUT_THIS_EVENT_READ_MORE}
        aria-label={formatMessage({ id: 'Read more about this event' })}
      >
        {formatMessage(READ_MORE_MESSAGE)}
      </button>
    ) : null;
  }, [handleShowFullEventOverview, formatMessage, hasMoreContentToShow, showFullEventOverview]);

  const readLessButton = useMemo(() => {
    return hasMoreContentToShow && showFullEventOverview ? (
      <div className={styles.readMoreWrapper}>
        <button
          className={styles.readMore}
          onClick={handleHideFullEventOverview}
          type="button"
          data-heap-id={LISTING_HEAP_IDS.ABOUT_THIS_EVENT_READ_LESS}
          aria-label={formatMessage({ id: 'Read less about this event' })}
        >
          {formatMessage({ id: 'Read less' })}
        </button>
      </div>
    ) : null;
  }, [handleHideFullEventOverview, formatMessage, hasMoreContentToShow, showFullEventOverview]);

  const displaySummary = useMemo(() => {
    const hasSummary = Boolean(sanitizedSummary);
    const originalModules = structuredContent?.modules || [];
    const firstModule = originalModules[0];

    if (hasSummary) {
      if (isValidTextModule(firstModule)) {
        return (
          <>
            <p dangerouslySetInnerHTML={{ __html: sanitizedSummary }} />
            <div dangerouslySetInnerHTML={{ __html: firstModule.text }} />
          </>
        );
      }
      return <p dangerouslySetInnerHTML={{ __html: sanitizedSummary }} />;
    } else {
      if (isValidTextModule(firstModule)) {
        return <div dangerouslySetInnerHTML={{ __html: firstModule.text }} />;
      } else {
        const firstTextModule = findFirstTextModule(originalModules);

        if (!firstTextModule?.text) {
          return null;
        }

        return <div dangerouslySetInnerHTML={{ __html: firstTextModule.text }} />;
      }
    }
  }, [sanitizedSummary, structuredContent]);

  if (!displaySummary) {
    return null;
  }

  const summaryClasses = classNames(styles.summary, {
    [styles.hidden]: showFullEventOverview,
  });

  const fadeOutEffect = hasMoreContentToShow && !showFullEventOverview ? <div className={styles.fadeOut}></div> : null;

  return (
    <>
      <div className={styles.title}>{formatMessage({ id: 'Overview' })}</div>

      <div className={styles.summaryWrapper}>
        <div ref={summaryRef} className={summaryClasses}>
          {displaySummary}
        </div>
        {fadeOutEffect}
      </div>
      {readMoreButton}

      <AboutThisEvent
        showFullEventOverview={showFullEventOverview}
        handleCloseModal={handleHideFullEventOverview}
        structuredContent={structuredContentWithSummary}
      />
      {readLessButton}
    </>
  );
});

Overview.displayName = 'Overview';
